import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Divider,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import SaveIcon from '@mui/icons-material/Save';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import getLetterTemplateQuery from '../../../../Apollo/queries/getLetterTemplate';
import GET_MAIL_SENDER from '../../../../Apollo/queries/getMailSender';
import createLetterTemplateMutation from '../../../../Apollo/mutations/createLetterTemplate';
import updateLetterTemplateMutation from '../../../../Apollo/mutations/updateLetterTemplate';
import {
  KNOWN_PLACEHOLDER_KEYS,
  PLACEHOLDER_CATEGORIES,
  PLACEHOLDER_EXAMPLE_VALUES,
} from '../../../../features/templates/catalog';
import useProtectedPlaceholderEditor from '../../../../features/templates/hooks/useProtectedPlaceholderEditor';
import {
  extractPlaceholderKeys,
  mergeFragmentedPlaceholders,
  replaceKnownPlaceholders,
} from '../../../../features/templates/utils/placeholderHtml';
import { PagePathname } from '../../../../routes/paths';
import useTemplateNavigationGuard from '../../../../features/templates/hooks/useTemplateNavigationGuard';
import {
  CREATE_CONTRACT_TEMPLATE,
  GET_CONTRACT_TEMPLATE,
  UPDATE_CONTRACT_TEMPLATE,
} from '../../../../features/templates/api/contractTemplates';

interface LetterTemplateFormProps {
  templateKind?: 'letter' | 'contract';
}

function LetterTemplateForm({ templateKind = 'letter' }: LetterTemplateFormProps) {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = id !== undefined && id !== 'neu';
  const isContract = templateKind === 'contract';
  const returnPath = isContract ? `${PagePathname.briefvorlagen}?bereich=vertragsvorlagen` : PagePathname.briefvorlagen;
  const editorRef = useRef<HTMLDivElement>(null);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [content, setContent] = useState('');
  const [nameError, setNameError] = useState('');
  const [contentError, setContentError] = useState('');
  const [isDirty, setIsDirty] = useState(false);
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
  const [pendingDestination, setPendingDestination] = useState(returnPath);
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);
  const allowNavigation = useTemplateNavigationGuard(isDirty);

  const {
    data: templateData,
    loading: templateLoading,
    error: templateError,
  } = useQuery(isContract ? GET_CONTRACT_TEMPLATE : getLetterTemplateQuery, {
    variables: { id: parseInt(id || '0', 10) },
    skip: !isEditMode,
    fetchPolicy: 'network-only',
  });
  const { data: mailSenderData } = useQuery(GET_MAIL_SENDER, {
    fetchPolicy: 'network-only',
    skip: isContract,
  });

  const [createTemplate] = useMutation(isContract ? CREATE_CONTRACT_TEMPLATE : createLetterTemplateMutation);
  const [updateTemplate] = useMutation(isContract ? UPDATE_CONTRACT_TEMPLATE : updateLetterTemplateMutation);

  const handleProtectedContentChange = useCallback((html: string) => {
    setContent(html);
    setIsDirty(true);
  }, []);

  const {
    format,
    getHtml,
    handleCut,
    handleDragEnd,
    handleDragStart,
    handleDrop,
    handleInput,
    handleKeyDown,
    handlePaste,
    insertPlaceholder,
    setHtml,
  } = useProtectedPlaceholderEditor({
    editorRef,
    knownKeys: KNOWN_PLACEHOLDER_KEYS,
    onChange: handleProtectedContentChange,
    readOnly: saving,
  });

  const loadedTemplate = isContract ? templateData?.getContractTemplate : templateData?.getLetterTemplate;
  const initializedTemplate = useRef<string>();
  useEffect(() => {
    const templateKey = `${templateKind}:${id}`;
    if (loadedTemplate && initializedTemplate.current !== templateKey) {
      const t = loadedTemplate;
      setName(t.name || '');
      setDescription(t.description || '');
      setContent(t.content || '');
      setHtml(t.content || '');
      setIsDirty(false);
      initializedTemplate.current = templateKey;
    }
  }, [id, loadedTemplate, setHtml, templateKind]);

  const validate = (): boolean => {
    let valid = true;
    setNameError('');
    setContentError('');

    if (!name.trim()) {
      setNameError('Bitte einen Namen eingeben');
      valid = false;
    }

    const plainContent = editorRef.current?.textContent || '';
    if (!plainContent.trim()) {
      setContentError('Bitte einen Inhalt eingeben');
      valid = false;
    }

    return valid;
  };

  const handleSave = async () => {
    if (saving || !validate()) return;
    setSaveError('');
    setSaving(true);

    const editorContent = editorRef.current ? getHtml() : content;
    const variables = extractPlaceholderKeys(mergeFragmentedPlaceholders(editorContent, KNOWN_PLACEHOLDER_KEYS));

    const input = {
      name: name.trim(),
      description: description.trim() || null,
      content: editorContent,
      variables_used: variables.length > 0 ? variables : null,
    };

    try {
      if (isEditMode) {
        await updateTemplate({
          variables: { input, id: parseInt(id!, 10) },
        });
      } else {
        await createTemplate({
          variables: { input },
        });
      }
      allowNavigation();
      setIsDirty(false);
      navigate(returnPath);
    } catch (err: unknown) {
      const error = err as { message?: string };
      if (error.message?.includes('DUPLICATE_NAME') || error.message?.includes('existiert bereits')) {
        setNameError(
          isContract
            ? 'Eine Vertragsvorlage mit diesem Namen existiert bereits'
            : 'Eine Vorlage mit diesem Namen existiert bereits',
        );
      } else {
        setSaveError('Die Vorlage konnte nicht gespeichert werden. Bitte versuche es erneut.');
      }
    } finally {
      setSaving(false);
    }
  };

  const requestNavigation = (destination: string) => {
    if (isDirty) {
      setPendingDestination(destination);
      setDiscardDialogOpen(true);
    } else {
      navigate(destination);
    }
  };

  const handleCancel = () => requestNavigation(returnPath);

  const today = new Date();
  const formattedDate = `${String(today.getDate()).padStart(2, '0')}.${String(today.getMonth() + 1).padStart(2, '0')}.${today.getFullYear()}`;
  let breadcrumbLabel = 'Neue Vorlage';
  if (isContract) breadcrumbLabel = 'Neue Vertragsvorlage';
  if (isEditMode) breadcrumbLabel = name || 'Bearbeiten';
  let headerIcon = <MailOutlineIcon sx={{ fontSize: 28, color: '#4ADE80' }} />;
  if (isEditMode) headerIcon = <EditIcon sx={{ fontSize: 28, color: '#4ADE80' }} />;
  if (!isEditMode && isContract) {
    headerIcon = <DescriptionOutlinedIcon sx={{ fontSize: 28, color: '#4ADE80' }} />;
  }
  let headerTitle = 'Neue Vorlage erstellen';
  if (isContract) headerTitle = 'Neue Vertragsvorlage';
  if (isEditMode) headerTitle = name || (isContract ? 'Vertragsvorlage bearbeiten' : 'Vorlage bearbeiten');

  return (
    <>
      {templateLoading && (
        <Box sx={{ p: 6, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress aria-label="Vorlage wird geladen" />
        </Box>
      )}
      {templateError && (
        <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
          <Alert severity="error">Die Vorlage konnte nicht geladen werden.</Alert>
          <Button onClick={() => navigate(returnPath)} sx={{ mt: 2 }}>
            Zurück zur Übersicht
          </Button>
        </Box>
      )}
      {/* Keep the editor mounted so its native placeholder protection also binds during async loading. */}
      <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto', display: templateLoading || templateError ? 'none' : 'block' }}>
        {/* Breadcrumb */}
        <Box sx={{ mb: 1 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            <Box
              component="span"
              sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
              onClick={() => requestNavigation(PagePathname.einstellung)}
            >
              Einstellungen
            </Box>
            {' › '}
            <Box
              component="span"
              sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
              onClick={handleCancel}
            >
              Vorlagen
            </Box>
            {' › '}
            <Box component="span" sx={{ color: 'text.primary' }}>
              {breadcrumbLabel}
            </Box>
          </Typography>
        </Box>

        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {headerIcon}
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              {headerTitle}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={handleCancel}
              disabled={saving}
              sx={{ color: 'text.secondary', borderColor: 'divider' }}
            >
              Abbrechen
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={saving}
              sx={{
                bgcolor: '#4ADE80',
                color: '#000',
                fontWeight: 'bold',
                '&:hover': { bgcolor: '#3CC970' },
              }}
            >
              {saving ? 'Wird gespeichert …' : 'Speichern'}
            </Button>
          </Box>
        </Box>

        {/* Split View */}
        {saveError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {saveError}
          </Alert>
        )}
        <Box sx={{ display: 'flex', gap: 3, minHeight: 600 }}>
          {/* Left Column: Form */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Template Name */}
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                textTransform: 'uppercase',
                fontWeight: 'bold',
                fontSize: '0.75rem',
                mb: 0.5,
                display: 'block',
              }}
            >
              Vorlagenname
            </Typography>
            <TextField
              fullWidth
              placeholder={
                isContract ? 'z. B. Beratervertrag Pro Holidays männlich' : 'z.B. Willkommensschreiben Bistro'
              }
              value={name}
              disabled={saving}
              onChange={(e) => {
                setName(e.target.value.substring(0, 120));
                setIsDirty(true);
                setNameError('');
              }}
              error={!!nameError}
              helperText={nameError}
              sx={{ mb: 2 }}
              inputProps={{ maxLength: 120, 'aria-label': 'Vorlagenname' }}
            />

            {/* Description */}
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                textTransform: 'uppercase',
                fontWeight: 'bold',
                fontSize: '0.75rem',
                mb: 0.5,
                display: 'block',
              }}
            >
              Interne Beschreibung (optional)
            </Typography>
            <TextField
              fullWidth
              placeholder="Kurznotiz für das Team..."
              value={description}
              disabled={saving}
              onChange={(e) => {
                setDescription(e.target.value.substring(0, 250));
                setIsDirty(true);
              }}
              sx={{ mb: 2 }}
              inputProps={{ maxLength: 250, 'aria-label': 'Interne Beschreibung (optional)' }}
            />

            {/* Placeholders */}
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                textTransform: 'uppercase',
                fontWeight: 'bold',
                fontSize: '0.75rem',
                mb: 1,
                display: 'block',
              }}
            >
              Platzhalter einfügen
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
              {PLACEHOLDER_CATEGORIES.map((cat) =>
                cat.placeholders.map((ph) => (
                  <Tooltip key={ph} title={cat.label} arrow placement="top">
                    <Chip
                      label={`{{${ph}}}`}
                      disabled={saving}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => insertPlaceholder(ph)}
                      size="small"
                      sx={{
                        bgcolor: 'rgba(74, 222, 128, 0.15)',
                        color: '#4ADE80',
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        borderRadius: '16px',
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'rgba(74, 222, 128, 0.3)' },
                      }}
                    />
                  </Tooltip>
                )),
              )}
            </Box>

            {/* Text Editor */}
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                textTransform: 'uppercase',
                fontWeight: 'bold',
                fontSize: '0.75rem',
                mb: 0.5,
                display: 'block',
              }}
            >
              Inhalt / Text
            </Typography>

            {/* Toolbar */}
            <Box
              sx={{
                display: 'flex',
                gap: 0.5,
                p: 0.5,
                borderRadius: '4px 4px 0 0',
                border: '1px solid',
                borderColor: contentError ? 'error.main' : 'divider',
                borderBottom: 'none',
                bgcolor: 'action.hover',
              }}
            >
              <Tooltip title="Fett">
                <IconButton
                  size="small"
                  disabled={saving}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => format('bold')}
                >
                  <FormatBoldIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Kursiv">
                <IconButton
                  size="small"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => format('italic')}
                  disabled={saving}
                >
                  <FormatItalicIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Unterstrichen">
                <IconButton
                  size="small"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => format('underline')}
                  disabled={saving}
                >
                  <FormatUnderlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Linksbündig ausrichten">
                <IconButton
                  size="small"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => format('justifyLeft')}
                  disabled={saving}
                >
                  <FormatAlignLeftIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Aufzählung">
                <IconButton
                  size="small"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => format('insertUnorderedList')}
                  disabled={saving}
                >
                  <FormatListBulletedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Editor Area */}
            <Box
              ref={editorRef}
              contentEditable={!saving}
              aria-readonly={saving}
              role="textbox"
              aria-label={isContract ? 'Vertragstext' : 'Inhalt / Text'}
              aria-multiline="true"
              suppressContentEditableWarning
              onInput={handleInput}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              onCut={handleCut}
              onDragEnd={handleDragEnd}
              onDragStart={handleDragStart}
              onDrop={handleDrop}
              sx={{
                minHeight: 200,
                p: 2,
                border: '1px solid',
                borderColor: contentError ? 'error.main' : 'divider',
                borderRadius: '0 0 4px 4px',
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '13px',
                lineHeight: 1.7,
                outline: 'none',
                resize: 'vertical',
                overflow: 'auto',
                '&:focus': {
                  borderColor: '#4ADE80',
                },
              }}
            />
            {contentError && (
              <Typography variant="caption" sx={{ color: 'error.main', mt: 0.5, display: 'block' }}>
                {contentError}
              </Typography>
            )}
          </Box>

          {/* Right Column: Live Preview */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              bgcolor: 'background.default',
              borderRadius: 2,
              p: 3,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <FiberManualRecordIcon sx={{ fontSize: 10, color: '#4ADE80' }} />
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  textTransform: 'uppercase',
                  fontWeight: 'bold',
                  fontSize: '0.75rem',
                  letterSpacing: 1,
                }}
              >
                {isContract ? 'Vertragsvorschau (Beispieldaten)' : 'Live-Vorschau (Beispieldaten)'}
              </Typography>
            </Box>

            {/* Preview Box */}
            <Box
              data-testid={isContract ? 'contract-template-preview-card' : 'letter-template-preview-card'}
              sx={{
                bgcolor: '#ffffff',
                borderRadius: 2,
                p: 4,
                color: '#1a1a1a',
                minHeight: 400,
              }}
            >
              {!isContract && (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography sx={{ fontWeight: 'bold', fontSize: '1.25rem', color: '#1a1a1a' }}>rita.ag</Typography>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography sx={{ fontSize: '0.8rem', color: '#666' }}>
                        {mailSenderData?.getMailSender ?? ''}
                      </Typography>
                      <Typography sx={{ fontSize: '0.8rem', color: '#666' }}>{formattedDate}</Typography>
                    </Box>
                  </Box>
                  <Divider sx={{ mb: 3, bgcolor: '#e0e0e0' }} />
                </>
              )}

              {/* Preview Content */}
              <Box
                sx={{
                  fontFamily: '"DM Sans", sans-serif',
                  fontSize: '13px',
                  lineHeight: 1.7,
                  color: '#333',
                  '& p': { marginBottom: '0.5em', marginTop: 0 },
                  '& ul': { paddingLeft: '1.5em' },
                }}
                dangerouslySetInnerHTML={{
                  __html: replaceKnownPlaceholders(content, PLACEHOLDER_EXAMPLE_VALUES, KNOWN_PLACEHOLDER_KEYS),
                }}
              />
            </Box>
          </Box>
        </Box>

        {/* Discard Dialog */}
        <Dialog open={discardDialogOpen} onClose={() => setDiscardDialogOpen(false)}>
          <DialogTitle>Bearbeitung abbrechen?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Möchtest du die Bearbeitung abbrechen? Alle nicht gespeicherten Eingaben gehen verloren.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setDiscardDialogOpen(false)}
              variant="contained"
              sx={{
                bgcolor: '#4ADE80',
                color: '#000',
                '&:hover': { bgcolor: '#3CC970' },
              }}
            >
              Weiterbearbeiten
            </Button>
            <Button
              onClick={() => {
                allowNavigation();
                setIsDirty(false);
                navigate(pendingDestination);
              }}
              sx={{ color: 'text.secondary', textTransform: 'none' }}
            >
              Verwerfen und zurück
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
}

export default LetterTemplateForm;
